import { SEOConfig } from '../types/seo';

function getBaseUrl(): string {
  // Prefer runtime origin in-browser (correct for any host, including previews).
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }

  // Optional build-time override for crawlers/static contexts.
  const explicit = import.meta.env.VITE_PUBLIC_ORIGIN as string | undefined;
  if (explicit && typeof explicit === 'string') {
    return explicit.replace(/\/$/, '');
  }

  // Build-time fallback (must not rely on process.env in the browser bundle).
  return import.meta.env.PROD ? 'https://app.safepsy.com' : 'http://localhost:5173';
}

const baseUrl = getBaseUrl();

const defaultImage = `${baseUrl}/Logotransparent.png`;

export const seoConfig: Record<string, SEOConfig> = {
  '/': {
    title: 'SafePsy | Sage, your private AI companion for thought digestion',
    description:
      'Meet Sage, your private AI companion for thought digestion. Chat instantly with no sign-in, or use decentralized identity for encrypted conversations you control. We never train on your data.',
    keywords:
      'SafePsy, Sage, AI companion, thought digestion, mental wellness, privacy-first, encrypted chat, decentralized identity, private mental health support',
    ogTitle: 'SafePsy | Sage, your private AI companion for thought digestion',
    ogDescription:
      'Sage, your private AI companion for thought digestion. Reflection and between-session support with blockchain-secured identity and user-controlled encryption.',
    ogImage: defaultImage,
    ogUrl: baseUrl,
    twitterTitle: 'SafePsy — Private Mental Wellness Support',
    twitterDescription:
      'Meet Sage, your private AI companion for thought digestion. Chat without sign-in or use your wallet for encrypted history. We never train on your data.',
    twitterImage: defaultImage,
    twitterCard: 'summary_large_image',
    canonical: baseUrl,
  },
  '/feedback': {
    title: 'Beta Feedback - SafePsy',
    description: 'Share your feedback on SafePsy beta experience and help improve Sage.',
    keywords: 'SafePsy feedback, beta feedback, Sage feedback',
    ogTitle: 'Beta Feedback - SafePsy',
    ogDescription: 'Share your feedback on SafePsy beta experience and help improve Sage.',
    ogImage: defaultImage,
    ogUrl: `${baseUrl}/feedback`,
    twitterTitle: 'Beta Feedback - SafePsy',
    twitterDescription: 'Share your feedback on SafePsy beta experience and help improve Sage.',
    twitterImage: defaultImage,
    twitterCard: 'summary',
    canonical: `${baseUrl}/feedback`,
  },
  '/explore': {
    title: 'Explore SafePsy | Mission, values, and technology',
    description: 'Learn about SafePsy\'s mission to revolutionize mental health care with privacy-first technology. Meet our team and discover our commitment to ethical, secure therapy.',
    keywords: 'SafePsy explore, about SafePsy, mental health team, privacy-first therapy, ethical therapy, secure counseling, mental health mission',
    ogTitle: 'Explore SafePsy | Privacy-First Mental Health Platform',
    ogDescription: 'Learn about SafePsy\'s mission to revolutionize mental health care with privacy-first technology.',
    ogImage: defaultImage,
    ogUrl: `${baseUrl}/explore`,
    twitterTitle: 'Explore SafePsy | Privacy-First Mental Health',
    twitterDescription: 'Learn about SafePsy\'s mission to revolutionize mental health care with privacy-first technology.',
    twitterImage: defaultImage,
    twitterCard: 'summary_large_image',
    canonical: `${baseUrl}/explore`,
  },
  '/sap-policy': {
    title: 'Security & Privacy Policy - SafePsy',
    description: 'Comprehensive security and privacy policy for SafePsy. Learn how we protect your mental health data with blockchain technology and privacy-by-design principles.',
    keywords: 'privacy policy, security policy, data protection, GDPR, mental health privacy, blockchain security, privacy by design',
    ogTitle: 'Security & Privacy Policy - SafePsy',
    ogDescription: 'Comprehensive security and privacy policy for SafePsy. Learn how we protect your mental health data.',
    ogImage: defaultImage,
    ogUrl: `${baseUrl}/sap-policy`,
    twitterTitle: 'Security & Privacy Policy - SafePsy',
    twitterDescription: 'Comprehensive security and privacy policy for SafePsy. Learn how we protect your mental health data.',
    twitterImage: defaultImage,
    twitterCard: 'summary',
    canonical: `${baseUrl}/sap-policy`,
  },
  '/contact-us': {
    title: 'Contact Us - SafePsy | Get in Touch',
    description: 'Contact SafePsy for support, partnerships, or questions about our privacy-first mental health platform. We\'re here to help with your mental health journey.',
    keywords: 'contact SafePsy, mental health support, therapy questions, partnership, customer support, mental health help',
    ogTitle: 'Contact SafePsy - Get in Touch',
    ogDescription: 'Contact SafePsy for support, partnerships, or questions about our privacy-first mental health platform.',
    ogImage: defaultImage,
    ogUrl: `${baseUrl}/contact-us`,
    twitterTitle: 'Contact SafePsy - Get in Touch',
    twitterDescription: 'Contact SafePsy for support, partnerships, or questions about our privacy-first mental health platform.',
    twitterImage: defaultImage,
    twitterCard: 'summary',
    canonical: `${baseUrl}/contact-us`,
  },
  '/support': {
    title: 'Support - SafePsy | Help & Common Issues',
    description:
      'Get help with SafePsy: login and account, onboarding, billing, privacy, and deleting or exporting your data. Contact us if you need more assistance.',
    keywords:
      'SafePsy support, help, account login, onboarding, billing, privacy, delete account, export data, GDPR',
    ogTitle: 'Support - SafePsy',
    ogDescription:
      'Help with login, onboarding, billing, privacy, and data deletion or export for SafePsy.',
    ogImage: defaultImage,
    ogUrl: `${baseUrl}/support`,
    twitterTitle: 'Support - SafePsy',
    twitterDescription:
      'Help with login, onboarding, billing, privacy, and data deletion or export.',
    twitterImage: defaultImage,
    twitterCard: 'summary',
    canonical: `${baseUrl}/support`,
  },
  '/faq': {
    title: 'FAQ - SafePsy | Frequently Asked Questions',
    description: 'Frequently asked questions about SafePsy: privacy-first mental health, data protection, AI therapy, wallet and beta access, pricing, and support.',
    keywords: 'SafePsy FAQ, mental health questions, privacy therapy, AI therapy, data protection, support',
    ogTitle: 'FAQ - SafePsy | Frequently Asked Questions',
    ogDescription: 'Frequently asked questions about SafePsy: privacy-first mental health, data protection, and support.',
    ogImage: defaultImage,
    ogUrl: `${baseUrl}/faq`,
    twitterTitle: 'FAQ - SafePsy',
    twitterDescription: 'Frequently asked questions about SafePsy: privacy-first mental health, data protection, and support.',
    twitterImage: defaultImage,
    twitterCard: 'summary',
    canonical: `${baseUrl}/faq`,
  },
  '/register': {
    title: 'Register - SafePsy | Join the waitlist',
    description:
      'Create your SafePsy account by joining the waitlist. Get early access and product updates to our privacy-first mental wellness platform.',
    keywords: 'SafePsy register, SafePsy waitlist, early access, privacy-first mental wellness',
    ogTitle: 'Register - SafePsy',
    ogDescription: 'Join the SafePsy waitlist to get early access and product updates.',
    ogImage: defaultImage,
    ogUrl: `${baseUrl}/register`,
    twitterTitle: 'Register - SafePsy',
    twitterDescription: 'Join the SafePsy waitlist to get early access and product updates.',
    twitterImage: defaultImage,
    twitterCard: 'summary',
    canonical: `${baseUrl}/register`,
  },
  '/app/about-me': {
    title: 'My ID token - SafePsy | Identity & Chat Data',
    description: 'View and manage your SafePsy ID token and encrypted chat data. Export or manage your encrypted conversation history.',
    keywords: 'ID token profile, SafePsy profile, chat data, digital identity, data export',
    ogTitle: 'My ID token - SafePsy',
    ogDescription: 'View and manage your SafePsy ID token and encrypted chat data.',
    ogImage: defaultImage,
    ogUrl: `${baseUrl}/app/about-me`,
    twitterTitle: 'My ID token - SafePsy',
    twitterDescription: 'View and manage your SafePsy ID token and encrypted chat data.',
    twitterImage: defaultImage,
    twitterCard: 'summary',
    canonical: `${baseUrl}/app/about-me`,
  },
  '/profile': {
    title: 'My ID token - SafePsy | Identity & Chat Data',
    description: 'View and manage your SafePsy ID token and encrypted chat data. Export or manage your encrypted conversation history.',
    keywords: 'ID token profile, SafePsy profile, chat data, digital identity, data export',
    ogTitle: 'My ID token - SafePsy',
    ogDescription: 'View and manage your SafePsy ID token and encrypted chat data.',
    ogImage: defaultImage,
    ogUrl: `${baseUrl}/profile`,
    twitterTitle: 'My ID token - SafePsy',
    twitterDescription: 'View and manage your SafePsy ID token and encrypted chat data.',
    twitterImage: defaultImage,
    twitterCard: 'summary',
    canonical: `${baseUrl}/profile`,
  },
  '/dpia': {
    title: 'Data Protection Impact Assessment - SafePsy',
    description: 'Comprehensive Data Protection Impact Assessment (DPIA) for SafePsy. Learn how we assess and mitigate privacy risks in our mental health platform.',
    keywords: 'DPIA, data protection impact assessment, privacy assessment, GDPR compliance, mental health data protection',
    ogTitle: 'Data Protection Impact Assessment - SafePsy',
    ogDescription: 'Comprehensive DPIA for SafePsy. Learn how we assess and mitigate privacy risks in our mental health platform.',
    ogImage: defaultImage,
    ogUrl: `${baseUrl}/dpia`,
    twitterTitle: 'DPIA - SafePsy',
    twitterDescription: 'Comprehensive Data Protection Impact Assessment for SafePsy.',
    twitterImage: defaultImage,
    twitterCard: 'summary',
    canonical: `${baseUrl}/dpia`,
  },
  '/about-me': {
    title: 'About Me - SafePsy | Founder & Team',
    description: 'Learn about the SafePsy team and our commitment to revolutionizing mental health care with privacy-first technology and ethical AI.',
    keywords: 'SafePsy team, mental health founders, therapy team, privacy advocates, mental health professionals',
    ogTitle: 'About Me - SafePsy Team',
    ogDescription: 'Learn about the SafePsy team and our commitment to revolutionizing mental health care.',
    ogImage: defaultImage,
    ogUrl: `${baseUrl}/about-me`,
    twitterTitle: 'About Me - SafePsy Team',
    twitterDescription: 'Learn about the SafePsy team and our commitment to revolutionizing mental health care.',
    twitterImage: defaultImage,
    twitterCard: 'summary_large_image',
    canonical: `${baseUrl}/about-me`,
  },
  '/tos': {
    title: 'Terms of Service - SafePsy',
    description: 'Read SafePsy\'s Terms of Service. Understand the terms and conditions for using our privacy-first mental health platform.',
    keywords: 'terms of service, terms and conditions, legal terms, therapy platform terms, mental health service terms',
    ogTitle: 'Terms of Service - SafePsy',
    ogDescription: 'Read SafePsy\'s Terms of Service and understand the terms for using our platform.',
    ogImage: defaultImage,
    ogUrl: `${baseUrl}/tos`,
    twitterTitle: 'Terms of Service - SafePsy',
    twitterDescription: 'Read SafePsy\'s Terms of Service and understand the terms for using our platform.',
    twitterImage: defaultImage,
    twitterCard: 'summary',
    canonical: `${baseUrl}/tos`,
  },
  '/cookies': {
    title: 'Cookie Policy - SafePsy | Privacy & Cookie Information',
    description: 'Learn about SafePsy\'s cookie policy and how we use cookies to enhance your experience while maintaining your privacy.',
    keywords: 'cookie policy, cookies, privacy cookies, GDPR cookies, website cookies, tracking cookies',
    ogTitle: 'Cookie Policy - SafePsy',
    ogDescription: 'Learn about SafePsy\'s cookie policy and how we use cookies while maintaining your privacy.',
    ogImage: defaultImage,
    ogUrl: `${baseUrl}/cookies`,
    twitterTitle: 'Cookie Policy - SafePsy',
    twitterDescription: 'Learn about SafePsy\'s cookie policy and how we use cookies while maintaining your privacy.',
    twitterImage: defaultImage,
    twitterCard: 'summary',
    canonical: `${baseUrl}/cookies`,
  },
  '/maintenance': {
    title: 'Maintenance Mode - SafePsy',
    description: 'SafePsy is currently under maintenance. We\'ll be back soon with improvements to serve you better.',
    keywords: 'maintenance, site maintenance, SafePsy maintenance',
    ogTitle: 'Maintenance Mode - SafePsy',
    ogDescription: 'SafePsy is currently under maintenance. We\'ll be back soon.',
    ogImage: defaultImage,
    ogUrl: `${baseUrl}/maintenance`,
    twitterTitle: 'Maintenance Mode - SafePsy',
    twitterDescription: 'SafePsy is currently under maintenance. We\'ll be back soon.',
    twitterImage: defaultImage,
    twitterCard: 'summary',
    canonical: `${baseUrl}/maintenance`,
  },
  '/status': {
    title: 'System Status - SafePsy | Live Status Check',
    description: 'Check the current status of SafePsy\'s systems. Monitor live status and service availability.',
    keywords: 'system status, health check, service status, SafePsy status, uptime, availability',
    ogTitle: 'System Status - SafePsy',
    ogDescription: 'Check the current status of SafePsy\'s systems and service availability.',
    ogImage: defaultImage,
    ogUrl: `${baseUrl}/status`,
    twitterTitle: 'System Status - SafePsy',
    twitterDescription: 'Check the current status of SafePsy\'s systems and service availability.',
    twitterImage: defaultImage,
    twitterCard: 'summary',
    canonical: `${baseUrl}/status`,
  },
  '/500': {
    title: 'Server Error - SafePsy',
    description: 'We encountered a server error. Please try again later or contact support if the problem persists.',
    keywords: 'server error, error 500, SafePsy error',
    ogTitle: 'Server Error - SafePsy',
    ogDescription: 'We encountered a server error. Please try again later.',
    ogImage: defaultImage,
    ogUrl: `${baseUrl}/500`,
    twitterTitle: 'Server Error - SafePsy',
    twitterDescription: 'We encountered a server error. Please try again later.',
    twitterImage: defaultImage,
    twitterCard: 'summary',
    canonical: `${baseUrl}/500`,
  },
  '/internal/admin/7x9k2m4p8q1r5t3v6w0y2z4a6b8c0d2e4f6': {
    title: 'Administrative Portal - SafePsy | Secure Access',
    description: 'Secure administrative portal with comprehensive accessibility features and SEO optimization. WCAG 2.1 AAA compliant with full keyboard navigation, screen reader support, and semantic HTML5 structure.',
    keywords: 'administrative portal, secure access, accessibility, WCAG AAA, SEO optimization, semantic HTML, keyboard navigation',
    ogTitle: 'Administrative Portal - SafePsy',
    ogDescription: 'Secure administrative portal with comprehensive accessibility features and SEO optimization. WCAG 2.1 AAA compliant.',
    ogImage: defaultImage,
    ogUrl: `${baseUrl}/internal/admin/7x9k2m4p8q1r5t3v6w0y2z4a6b8c0d2e4f6`,
    twitterTitle: 'Administrative Portal - SafePsy',
    twitterDescription: 'Secure administrative portal with comprehensive accessibility features and SEO optimization.',
    twitterImage: defaultImage,
    twitterCard: 'summary_large_image',
    canonical: `${baseUrl}/internal/admin/7x9k2m4p8q1r5t3v6w0y2z4a6b8c0d2e4f6`,
    noindex: true, // Hidden from search engines
  },
  '/admin/analytics': {
    title: 'Analytics Dashboard - SafePsy | Admin',
    description:
      'Admin-protected onboarding funnel dashboard for SafePsy. Not indexed — access is gated by the admin secret.',
    keywords: '',
    ogTitle: 'Analytics Dashboard - SafePsy',
    ogDescription: 'Admin-protected onboarding funnel dashboard for SafePsy.',
    ogImage: defaultImage,
    ogUrl: `${baseUrl}/admin/analytics`,
    twitterTitle: 'SafePsy Analytics Dashboard',
    twitterDescription: 'Admin-protected onboarding funnel dashboard for SafePsy.',
    twitterImage: defaultImage,
    twitterCard: 'summary',
    canonical: `${baseUrl}/admin/analytics`,
    noindex: true, // Admin-only; never index the login gate (mirrors the /internal/admin entry)
  },
  '/blog': {
    title: 'Blog - SafePsy | Privacy-First AI Therapy Insights',
    description: 'Expert articles on AI therapy, mental health data privacy, blockchain identity, and secure mental wellness tools.',
    keywords: 'mental health blog, AI therapy insights, data privacy mental health, safePsy blog, encrypted therapy, AI mental health, blockchain therapy',
    ogTitle: 'SafePsy Blog',
    ogDescription: 'Expert articles on AI therapy, mental health data privacy, blockchain identity, and secure mental wellness tools.',
    ogImage: defaultImage,
    ogUrl: `${baseUrl}/blog`,
    twitterTitle: 'SafePsy Blog',
    twitterDescription: 'Expert articles on AI therapy, mental health data privacy, blockchain identity, and secure mental wellness tools.',
    twitterImage: defaultImage,
    twitterCard: 'summary_large_image',
    canonical: `${baseUrl}/blog`,
  },
  '*': {
    title: 'Page Not Found - SafePsy | 404 Error',
    description: 'The page you\'re looking for doesn\'t exist. Return to SafePsy homepage to continue your mental health journey.',
    keywords: '404, page not found, error page, SafePsy',
    ogTitle: 'Page Not Found - SafePsy',
    ogDescription: 'The page you\'re looking for doesn\'t exist. Return to SafePsy homepage.',
    ogImage: defaultImage,
    ogUrl: `${baseUrl}/404`,
    twitterTitle: 'Page Not Found - SafePsy',
    twitterDescription: 'The page you\'re looking for doesn\'t exist. Return to SafePsy homepage.',
    twitterImage: defaultImage,
    twitterCard: 'summary',
    canonical: `${baseUrl}/404`,
  },
};
